import { useTranslate } from '@repo/shared/hooks'
import { Button, Col, Icon, Row, Typography } from '@repo/ui/components'
import React, { useRef, useState } from 'react'
import { IUploadedFile } from './job-qualifications.types'

interface JobQualificationsFileListProps {
  files: IUploadedFile[]
  onFileDelete: (fileId: string) => void
  onAddMoreFiles: (files: File[]) => void
}

export const JobQualificationsFileList = (
  props: JobQualificationsFileListProps,
) => {
  const { files, onFileDelete, onAddMoreFiles } = props
  const { t } = useTranslate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
  }

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click()
  }

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []) as File[]
    if (files.length > 0) {
      onAddMoreFiles(files)
    }
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const files = Array.from(event.dataTransfer.files || []) as File[]
    if (files.length > 0) {
      onAddMoreFiles(files)
    }
  }

  return (
    <Col className='gap-sm'>
      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept='.pdf,.jpg,.jpeg,.png,.doc,.docx'
        onChange={handleFileInputChange}
        className='hidden'
      />
      {files.map((file) => (
        <Row key={file.id} className='justify-between'>
          <Row className='gap-md items-center'>
            <Row className='flex-1 justify-between'>
              <Typography
                size='xs'
                className='max-w-[250px] text-ellipsis overflow-hidden whitespace-nowrap'
              >
                {file.name}
              </Typography>
              <Typography size='xs' className='text-neutral-surface-60'>
                {formatFileSize(file.size)}
              </Typography>
            </Row>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => onFileDelete(file.id)}
              color='negative'
              className='px-md'
            >
              {t('common_delete')}
            </Button>
          </Row>
        </Row>
      ))}

      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Button
          variant='ghost'
          onClick={handleAddMoreFiles}
          className={`w-full transition-colors ${
            isDragging ? 'bg-base-surface-10 border-dashed' : ''
          }`}
        >
          <Icon name='Plus' size='sm' className='mr-xs' />
          {t('agents_job_qualifications_upload_more')}
        </Button>
      </div>
    </Col>
  )
}
